/**
 * Lightweight i18n for IT (default) / EN.
 *
 * Usage:
 *   const { t, lang, setLang } = useI18n();
 *   <h1>{t('common.dashboard')}</h1>
 *
 * Keys missing in EN dictionary fall back to IT (which is the source of truth).
 * Persisted in localStorage under "app_lang".
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const DICT = {
  it: {
    // ── Navbar / global ───────────────────────────────────────────
    'nav.cycle': 'Ciclo Economico',
    'nav.about': 'Chi sono',
    'nav.stocks': 'Analisi Titoli',
    'nav.projects_cta': 'I miei Progetti',
    'lang.switch': 'Lingua',
    'lang.it': 'Italiano',
    'lang.en': 'English',
    // ── Legal / consent ───────────────────────────────────────────
    'footer.privacy': 'Privacy',
    'footer.terms': 'Condizioni di vendita',
    'consent.privacy_lead': 'Ho letto e accetto',
    'consent.privacy_link': 'l’informativa privacy',
    // ── Dashboard ─────────────────────────────────────────────────
    'dashboard.title': 'Ciclo Economico',
    'dashboard.subtitle': 'Fase del ciclo e indicatori di mercato in tempo reale',
    'dashboard.last_updated': 'Aggiornato',
    'dashboard.error_loading': 'Errore nel caricamento',
    'dashboard.hint_select_index': "Seleziona un indice per visualizzarne l'andamento nel grafico",
    'dashboard.cycle_phase': 'Fase del Ciclo',
    'dashboard.cycle_stage': 'Stadio',
    // Cycle phases (returned by backend in IT)
    // ── Stock Analysis ────────────────────────────────────────────
    'stock.title': 'Analisi Titoli',
    'stock.subtitle': "Inserisci un ticker per un'analisi completa in stile Buffett",
    'stock.placeholder_ticker': 'Ticker o nome azienda (es. AAPL, Apple...)',
    'stock.analyze': 'Analizza',
    'stock.searching': 'Ricerca...',
    'stock.service_down': 'Servizio temporaneamente non disponibile.',
    'stock.service_down_detail_both': 'AI e provider dati offline.',
    'stock.service_down_detail_ai': 'Tutti i provider AI sono offline.',
    'stock.service_down_detail_data': 'Tutti i provider dati di mercato sono temporaneamente offline.',
    'stock.service_down_retry': 'Riprova tra qualche minuto.',
    'stock.maintenance': 'Analisi titoli in manutenzione.',
    'stock.maintenance_detail': 'Il servizio di analisi è temporaneamente sospeso per manutenzione. Tornerà disponibile a breve.',
    // Daily free
    // Beforeunload warning

    // ── Promo Day ──────────────────────────────────────────────────
    'promoday.eyebrow': 'Promo Day',
    'promoday.limited': 'A tempo limitato',
    'promoday.or': 'oppure',
    'promoday.headline': 'Presentati e ti mando qualche coupon.',
    'promoday.body': 'Due righe su chi sei e cosa fai. Mi fa piacere sapere chi c\'è dall\'altra parte, e in cambio ti giro dei coupon omaggio per l\'analisi titoli.',
    'promoday.label_name': 'Nome e cognome',
    'promoday.label_presentation': 'Chi sei e cosa fai',
    'promoday.placeholder_presentation': 'Cosa fai, da quanto investi, cosa ti interessa della dashboard...',
    'promoday.send': 'Invia richiesta',
    'promoday.sending': 'Invio in corso...',
    'promoday.sent': 'Richiesta inviata! Ti risponderò appena possibile.',
    'promoday.error': 'Errore durante l\'invio. Riprova.',
    'promoday.linkedin_cta': 'Scrivimi su LinkedIn',
    // ── Stock Report (PDF + UI) ───────────────────────────────────
  },

  en: {
    // ── Navbar / global ───────────────────────────────────────────
    'nav.cycle': 'Economic Cycle',
    'nav.about': 'About Me',
    'nav.stocks': 'Stock Analysis',
    'nav.projects_cta': 'My Projects',
    'lang.switch': 'Language',
    'lang.it': 'Italian',
    'lang.en': 'English',
    // ── Legal / consent ───────────────────────────────────────────
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms of sale',
    'consent.privacy_lead': 'I have read and accept the',
    'consent.privacy_link': 'privacy notice',
    // ── Dashboard ─────────────────────────────────────────────────
    'dashboard.title': 'Economic Cycle',
    'dashboard.subtitle': 'Cycle phase and live market indicators',
    'dashboard.last_updated': 'Updated',
    'dashboard.error_loading': 'Loading error',
    'dashboard.hint_select_index': 'Pick an index to view its chart',
    'dashboard.cycle_phase': 'Cycle Phase',
    'dashboard.cycle_stage': 'Stage',
    // Cycle phases
    // ── Stock Analysis ────────────────────────────────────────────
    'stock.title': 'Stock Analysis',
    'stock.subtitle': 'Enter a ticker for a full Buffett-style analysis',
    'stock.placeholder_ticker': 'Ticker or company name (e.g. AAPL, Apple...)',
    'stock.analyze': 'Analyze',
    'stock.searching': 'Searching...',
    'stock.service_down': 'Service temporarily unavailable.',
    'stock.service_down_detail_both': 'AI and data providers are offline.',
    'stock.service_down_detail_ai': 'All AI providers are offline.',
    'stock.service_down_detail_data': 'All market data providers are temporarily offline.',
    'stock.service_down_retry': 'Please retry in a few minutes.',
    'stock.maintenance': 'Stock analysis under maintenance.',
    'stock.maintenance_detail': 'The analysis service is temporarily suspended for maintenance. It will be back shortly.',
    // Daily free
    // Beforeunload warning

    // ── Promo Day ──────────────────────────────────────────────────
    'promoday.eyebrow': 'Promo Day',
    'promoday.limited': 'Limited time',
    'promoday.or': 'or',
    'promoday.headline': "Introduce yourself and I'll send you some coupons.",
    'promoday.body': "A couple of lines on who you are and what you do. I like knowing who is on the other side, and in return I'll send you free stock-analysis coupons.",
    'promoday.label_name': 'Full name',
    'promoday.label_presentation': 'Who you are and what you do',
    'promoday.placeholder_presentation': 'What you do, how long you have been investing, what interests you here...',
    'promoday.send': 'Send request',
    'promoday.sending': 'Sending...',
    'promoday.sent': "Request sent! I'll reply as soon as possible.",
    'promoday.error': 'Error during submission. Please retry.',
    'promoday.linkedin_cta': 'Message me on LinkedIn',
    // ── Stock Report (PDF + UI) ───────────────────────────────────
  },
};

const I18nContext = createContext({ lang: 'it', setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    // Priority 1: explicit ?lang= URL query parameter (hreflang / shareable links).
    try {
      if (typeof window !== 'undefined') {
        const sp = new URLSearchParams(window.location.search);
        const q = (sp.get('lang') || '').toLowerCase();
        if (q === 'it' || q === 'en') {
          try { localStorage.setItem('app_lang', q); } catch (e) { /* ignore */ }
          return q;
        }
      }
    } catch (e) { /* ignore */ }
    // Priority 2: previously chosen language (localStorage).
    try {
      const saved = localStorage.getItem('app_lang');
      if (saved === 'it' || saved === 'en') return saved;
    } catch (e) { /* ignore */ }
    // navigator.languages is the user-preference list (most reliable),
    // navigator.language is the single primary one (fallback).
    try {
      const candidates = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
      for (const tag of candidates) {
        if (!tag) continue;
        const code = tag.toLowerCase().split('-')[0];
        if (code === 'it') return 'it';
        if (code === 'en') return 'en';
      }
      // Anything else → fall through to English (closest to "international").
      if (candidates.length && !candidates[0].toLowerCase().startsWith('it')) return 'en';
    } catch (e) { /* ignore */ }
    return 'it';
  });

  const setLang = useCallback((l) => {
    if (l !== 'it' && l !== 'en') return;
    setLangState(l);
    try { localStorage.setItem('app_lang', l); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const t = useCallback((key, fallback) => {
    const v = DICT[lang]?.[key] ?? DICT.it[key];
    if (v !== undefined) return v;
    return fallback !== undefined ? fallback : key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export default function useI18n() {
  return useContext(I18nContext);
}
